import ouibounce from 'ouibounce';
import { useEffect, useState } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { fireTrackEvent } from '~globals/tracking';
import useFeatureFlags from '~hooks/featureFlags/useFeatureFlags';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';
import { updateAnyLeadPopupShown } from '~redux/Actions/GlobalActions';
import store from '~redux/store';

import Generic from './layouts/Generic';
import NtmExit from './layouts/Ntm';

const PopupCtaListener = () => {
    const { pathname } = usePageContext().location;

    const isHiddenByFeatureFlag = useFeatureFlags().HideMedicareGuideSticky();

    const suppressEmailCapturePopup = useAppSelector(
        (state) => state.global.suppressEmailCapturePopup
    );

    const dispatch = useAppDispatch();

    const [openModalType, setOpenModalType] = useState('');

    const { isMobile } = useWindowSize();

    const idleEvents = ['click', 'mousemove', 'keypress', 'scroll'];
    const timeoutDuration = 60000;
    let time: NodeJS.Timeout;

    enum MODAL_TYPE {
        generic = 'generic',
        ntmExit = 'ntmExit',
    }

    const excludePathFragments = ['/partner/sutter/', '/enroll/medicare-supplement/'];
    const isExcludedPath = excludePathFragments.some((excludedPathFragment) =>
        pathname.includes(excludedPathFragment)
    );

    const handleExit = () => {
        if (isExcludedPath) return;

        const { leadToken, hasAnyLeadPopupShown } = store.getState().global;
        const isAnotherModalOpen = !!document.querySelector('[data-qa="ModalContainer"]');

        if (!openModalType && !leadToken && !hasAnyLeadPopupShown && !isAnotherModalOpen) {
            const isNtm = pathname.includes('/new-to-medicare/');
            let type: string;
            let linkName: string;

            if (isNtm) {
                type = MODAL_TYPE.ntmExit;
                linkName = 'get medicare guide';
            } else {
                type = MODAL_TYPE.generic;
                linkName = 'sign me up';
            }

            setOpenModalType(type);
            dispatch(updateAnyLeadPopupShown());
            document.removeEventListener('scroll', handleRapidScroll);
            fireTrackEvent({
                page: {
                    action: `popup: exit attempt: ${linkName}`,
                },
            });
        }
    };

    const handleRapidScroll = () => {
        if (!isMobile) return;
        if (isExcludedPath) return;

        const currentPos = window.scrollY;
        setTimeout(() => {
            if (window.scrollY + 100 < currentPos) handleExit();
        }, 50);
    };

    const handleIdle = () => {
        if (isMobile) return;
        if (isExcludedPath) return;

        const { leadToken, hasAnyLeadPopupShown } = store.getState().global;
        const isAnotherModalOpen = !!document.querySelector('[data-qa="ModalContainer"]');

        if (!openModalType && !leadToken && !hasAnyLeadPopupShown && !isAnotherModalOpen) {
            const isNtm = pathname.includes('/new-to-medicare/');
            let type: string;
            let linkName: string;

            if (isNtm) {
                type = MODAL_TYPE.ntmExit;
                linkName = 'sign me up';
            } else {
                type = MODAL_TYPE.generic;
                linkName = 'get medicare guide';
            }

            setOpenModalType(type);
            dispatch(updateAnyLeadPopupShown());
            idleEvents.forEach((event) => document.removeEventListener(event, resetTimer));
            fireTrackEvent({
                page: {
                    action: `popup: idle: ${linkName}`,
                },
            });
        }
    };

    const resetTimer = () => {
        if (time) {
            clearTimeout(time);
        }
        time = setTimeout(handleIdle, timeoutDuration);
    };

    const setIsOpen = (isOpen: boolean) => {
        if (!isOpen) setOpenModalType('');
    };

    useEffect(() => {
        // watch for mouse movement out of window
        ouibounce(null, {
            callback: handleExit,
            cookieName: 'ntm-guide-exit',
            aggressive: true,
            timer: 20000,
        });

        // watch for rapid scroll-up on mobile
        // document.addEventListener('scroll', handleRapidScroll, { passive: true });

        // begin inactivity countdown
        time = setTimeout(handleIdle, timeoutDuration);
        idleEvents.forEach((event) => {
            document.addEventListener(event, resetTimer);
        });
    }, []);

    if (!suppressEmailCapturePopup && !isHiddenByFeatureFlag) {
        if (pathname.includes('/new-to-medicare/')) {
            return (
                <>
                    <NtmExit isOpen={openModalType === MODAL_TYPE.ntmExit} setIsOpen={setIsOpen} />
                </>
            );
        }
        return <Generic isOpen={openModalType === MODAL_TYPE.generic} setIsOpen={setIsOpen} />;
    }
    return null;
};

export default PopupCtaListener;
